import { useNavigate, NavLink, useLocation } from "react-router-dom";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Clipboard,
  Package,
  Search,
  ScanLine,
  ScanBarcode,
  Store,
  Shield,
  LogOut,
  Settings,
  Languages,
  Check,
  ChevronsUpDown,
  Building2,
  Upload,
} from "lucide-react";
import { toast } from "sonner";
import { CameraScanner, type OCRResult } from "@/components/CameraScanner";

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  DropdownMenuPortal,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useAuth } from "@/contexts/AuthContext";
import { useIsSuperadmin } from "@/hooks/useUserRole";
import { useCanManageCompany } from "@/hooks/useCanManageCompany";
import { useCompanyNavTabs, type NavTabId } from "@/hooks/useCompanyNavTabs";
import { useCurrentCompany } from "@/hooks/useCurrentCompany";
import { useLocale } from "@/hooks/useLocale";
import { LOCALE_LABELS, type SupportedLocale } from "@/i18n";
import { avatarBgClass, initialsFor, displayNameFor } from "@/lib/avatarColor";
import { cn } from "@/lib/utils";

type Item = { id: NavTabId; label: string; icon: typeof Clipboard; path: string };

export function AppSidebar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { isSuperadmin, isLoading: superadminLoading } = useIsSuperadmin();
  const { isCompanyAdmin } = useCanManageCompany();
  const { isTabEnabled } = useCompanyNavTabs();
  const { name: companyName, memberships, id: activeCompanyId, setActive } = useCurrentCompany();
  const { current: currentLocale, available: availableLocales, setLocale } = useLocale();
  const { state, isMobile, setOpenMobile } = useSidebar();
  const collapsed = state === "collapsed" && !isMobile;

  const baseItems: Item[] = [
    { id: "work-orders", label: "Work Orders", icon: Clipboard, path: "/work-orders" },
    { id: "search", label: "Appliance Search", icon: Search, path: "/search" },
    { id: "scanner", label: "Pre Triage", icon: ScanLine, path: "/scanner" },
    { id: "inventory", label: "Inventory", icon: Package, path: "/inventory" },
    { id: "store", label: "Store", icon: Store, path: "/store" },
  ];

  const items: Item[] = baseItems.filter((i) => isTabEnabled(i.id));


  const isActive = (path: string) =>
    path === "/" ? location.pathname === "/" : location.pathname.startsWith(path);

  const handleNav = () => {
    if (isMobile) setOpenMobile(false);
  };

  const [scannerOpen, setScannerOpen] = useState(false);

  const handleCapture = (_typeShieldUrl: string, ocrResult?: OCRResult) => {
    if (ocrResult?.modelNumber) {
      navigate("/search", { state: { scannedTypeShieldUrl: _typeShieldUrl, scannedResult: ocrResult } });
    } else {
      toast.warning("Could not read a model number from the type shield");
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
    toast.success("Signed out");
  };

  const handleSwitchCompany = async (companyId: string) => {
    if (companyId === activeCompanyId) return;
    try {
      await setActive(companyId);
      window.location.reload();
    } catch (e) {
      console.error("Failed to switch company", e);
      toast.error("Failed to switch company");
    }
  };

  const displayName = displayNameFor(
    (user?.user_metadata as { full_name?: string } | undefined)?.full_name,
    user?.email,
  );
  const initials = initialsFor(
    (user?.user_metadata as { full_name?: string } | undefined)?.full_name,
    user?.email,
  );
  const avatarBg = avatarBgClass(user?.id ?? user?.email ?? null);
  const roleLabel = isSuperadmin ? "Superadmin" : isCompanyAdmin ? "Admin" : "Member";
  const canSwitchCompany = memberships.length > 1;

  // Secondary button style for the Scan / Search CTA
  const scanButtonClass =
    "bg-sidebar-accent text-sidebar-foreground border border-sidebar-border hover:bg-sidebar-accent/80 transition-colors";

  return (
    <TooltipProvider delayDuration={300}>
      <Sidebar collapsible="icon">
        {/* Header: trigger aligned to the icon column (centered 32x32 in 48px rail) */}
        <SidebarHeader className="p-2">
          <div
            className={cn(
              "flex items-center",
              collapsed ? "justify-center" : "justify-between gap-2",
            )}
          >
            {!collapsed && (
              <div className="flex items-center gap-2 min-w-0 pl-1">
                <div className="h-7 w-7 rounded-md bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold shrink-0">
                  F
                </div>
                <div className="flex flex-col min-w-0 leading-tight">
                  <span className="font-semibold text-sidebar-foreground truncate text-sm">
                    {companyName ?? "FixID"}
                  </span>
                  {companyName && (
                    <span className="text-[10px] text-sidebar-foreground/60 truncate">
                      powered by FixID
                    </span>
                  )}
                </div>
              </div>
            )}
            <SidebarTrigger className="h-8 w-8" />
          </div>
        </SidebarHeader>

        <SidebarContent>
          {/* Scan / Search secondary CTA */}
          <SidebarGroup className="pb-0">
            <SidebarGroupContent className="space-y-2">
              {collapsed ? (
                <div className="flex justify-center">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        onClick={() => setScannerOpen(true)}
                        aria-label="Scan / Search Appliance"
                        className={cn(
                          "flex h-8 w-8 items-center justify-center rounded-md",
                          scanButtonClass,
                        )}
                      >
                        <ScanBarcode className="h-4 w-4" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="right">Scan / Search Appliance</TooltipContent>
                  </Tooltip>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setScannerOpen(true)}
                  className={cn(
                    "flex h-9 w-full items-center gap-2 rounded-md px-3 text-sm font-medium text-left",
                    scanButtonClass,
                  )}
                >
                  <ScanBarcode className="h-4 w-4 shrink-0" />
                  <span className="truncate">Scan / Search Appliance</span>
                </button>
              )}

              {collapsed ? (
                <div className="flex justify-center">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        onClick={() => {
                          navigate("/batch-import");
                          handleNav();
                        }}
                        aria-label="Batch Type-Shield Import"
                        className={cn(
                          "flex h-8 w-8 items-center justify-center rounded-md",
                          scanButtonClass,
                        )}
                      >
                        <Upload className="h-4 w-4" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="right">Batch Type-Shield Import</TooltipContent>
                  </Tooltip>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    navigate("/batch-import");
                    handleNav();
                  }}
                  className={cn(
                    "flex h-9 w-full items-center gap-2 rounded-md px-3 text-sm font-medium text-left",
                    scanButtonClass,
                  )}
                >
                  <Upload className="h-4 w-4 shrink-0" />
                  <span className="truncate">Batch Type-Shield Import</span>
                </button>
              )}
            </SidebarGroupContent>
          </SidebarGroup>

          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                {items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <SidebarMenuItem key={item.id} className={collapsed ? "flex justify-center" : undefined}>
                      <SidebarMenuButton
                        asChild
                        isActive={isActive(item.path)}
                        tooltip={item.label}
                        className={collapsed ? "justify-center" : undefined}
                      >
                        <NavLink to={item.path} onClick={handleNav}>
                          <Icon className="h-4 w-4" />
                          {!collapsed && <span>{item.label}</span>}
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>

        <SidebarFooter className="p-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              {collapsed ? (
                <div className="flex justify-center">
                  <button
                    className={cn(
                      "h-8 w-8 rounded-full flex items-center justify-center font-semibold text-white text-xs hover:opacity-90 transition-opacity",
                      avatarBg,
                    )}
                    aria-label="Open profile menu"
                  >
                    {initials}
                  </button>
                </div>
              ) : (
                <button
                  className="flex items-center gap-3 w-full rounded-md text-left hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors p-2"
                  aria-label="Open profile menu"
                >
                  <div
                    className={cn(
                      "h-9 w-9 rounded-full flex items-center justify-center font-semibold text-white text-sm shrink-0",
                      avatarBg,
                    )}
                  >
                    {initials}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate text-sidebar-foreground">
                      {displayName}
                    </p>
                    <p className="text-xs text-sidebar-foreground/70 truncate">{roleLabel}</p>
                  </div>
                  <ChevronsUpDown className="h-4 w-4 opacity-60 shrink-0" />
                </button>
              )}
            </DropdownMenuTrigger>
            <DropdownMenuContent
              side="top"
              align="start"
              sideOffset={8}
              className="w-64"
            >
              {/* Company: submenu if switchable, otherwise read-only row */}
              {canSwitchCompany ? (
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>
                    <Building2 className="mr-2 h-4 w-4 text-muted-foreground" />
                    <span className="truncate">{companyName ?? "No company"}</span>
                  </DropdownMenuSubTrigger>
                  <DropdownMenuPortal>
                    <DropdownMenuSubContent className="max-w-64">
                      {memberships.map((m) => (
                        <DropdownMenuItem
                          key={m.id}
                          onSelect={() => handleSwitchCompany(m.id)}
                          className="flex items-center justify-between gap-2"
                        >
                          <span className="truncate">{m.name ?? "Unnamed company"}</span>
                          {m.id === activeCompanyId && (
                            <Check className="h-4 w-4 text-primary shrink-0" />
                          )}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuSubContent>
                  </DropdownMenuPortal>
                </DropdownMenuSub>
              ) : (
                <div className="flex items-center px-2 py-1.5 text-sm">
                  <Building2 className="mr-2 h-4 w-4 text-muted-foreground shrink-0" />
                  <span className="truncate">{companyName ?? "No company"}</span>
                </div>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={() => {
                  navigate("/settings");
                  if (isMobile) setOpenMobile(false);
                }}
              >
                <Settings className="mr-2 h-4 w-4" />
                Settings
              </DropdownMenuItem>
              {(!superadminLoading && isSuperadmin) && (
                <DropdownMenuItem
                  onSelect={() => {
                    navigate("/platform");
                    if (isMobile) setOpenMobile(false);
                  }}
                >
                  <Shield className="mr-2 h-4 w-4" />
                  Platform Settings
                </DropdownMenuItem>
              )}

              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  <Languages className="mr-2 h-4 w-4" />
                  <span>{t("language.label")}</span>
                </DropdownMenuSubTrigger>
                <DropdownMenuPortal>
                  <DropdownMenuSubContent>
                    {availableLocales.map((lng: SupportedLocale) => (
                      <DropdownMenuItem
                        key={lng}
                        onSelect={() => void setLocale(lng)}
                        className="flex items-center justify-between gap-2"
                      >
                        <span className="flex items-center gap-2">
                          <span>{LOCALE_LABELS[lng].flag}</span>
                          <span>{LOCALE_LABELS[lng].native}</span>
                        </span>
                        {lng === currentLocale && <Check className="h-4 w-4 text-primary" />}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuSubContent>
                </DropdownMenuPortal>
              </DropdownMenuSub>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={handleLogout}>
                <LogOut className="mr-2 h-4 w-4" />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarFooter>
      </Sidebar>

      <CameraScanner
        open={scannerOpen}
        onOpenChange={setScannerOpen}
        onCapture={handleCapture}
      />
    </TooltipProvider>
  );
}
